import React, { useCallback, useEffect, useState } from 'react';
import { api, formatApiError } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { useRealtime } from '@/context/RealtimeContext';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Bell, CheckCheck, Trash2, Lock, MessageSquarePlus, Send, UserPlus,
  FileText, ShieldCheck, CircleUserRound, Mail, Map, BellRing, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { pushSupported, getPushStatus, enablePush, disablePush } from '@/lib/push';

const TYPE_ICON = {
  stakeholder_registered: UserPlus,
  content_created: FileText,
  content_updated: FileText,
  profile_updated: CircleUserRound,
  account_status: ShieldCheck,
  message: Mail,
  map_health: Map,
};

function timeAgo(iso) {
  try {
    const d = new Date(iso); const s = Math.floor((Date.now() - d.getTime()) / 1000);
    if (s < 60) return 'adesso';
    if (s < 3600) return `${Math.floor(s / 60)} min fa`;
    if (s < 86400) return `${Math.floor(s / 3600)} h fa`;
    return d.toLocaleDateString('it-IT', { day: '2-digit', month: 'short' });
  } catch { return ''; }
}

export function NotificationsBell() {
  const { user } = useAuth();
  const { subscribe } = useRealtime();
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [compose, setCompose] = useState({ title: '', body: '', recipient_id: null, recipient_name: '' });
  const [push, setPush] = useState({ supported: false, subscribed: false });
  const [pushBusy, setPushBusy] = useState(false);

  const isAdmin = user?.role === 'admin';

  const refreshPush = useCallback(async () => {
    if (!pushSupported()) { setPush({ supported: false, subscribed: false }); return; }
    const s = await getPushStatus();
    setPush(s);
  }, []);

  const togglePush = async () => {
    setPushBusy(true);
    try {
      if (push.subscribed) {
        await disablePush();
        setPush((p) => ({ ...p, subscribed: false }));
        toast.success('Avvisi push disattivati');
      } else {
        await enablePush();
        setPush((p) => ({ ...p, subscribed: true }));
        toast.success('Avvisi push attivati su questo dispositivo');
      }
    } catch (e) {
      toast.error(e?.code === 'denied'
        ? 'Permesso negato dal browser. Abilita le notifiche dalle impostazioni del sito.'
        : (e?.message || 'Impossibile aggiornare gli avvisi push'));
    } finally {
      setPushBusy(false);
    }
  };

  const loadCount = useCallback(async () => {
    try { const { data } = await api.get('/notifications/unread-count'); setCount(data.count || 0); } catch { /* noop */ }
  }, []);
  const loadList = useCallback(async () => {
    try { const { data } = await api.get('/notifications', { params: { limit: 30 } }); setItems(data); } catch { /* noop */ }
  }, []);

  useEffect(() => { if (user) { loadCount(); } }, [user, loadCount]);
  useEffect(() => { if (open) { loadList(); refreshPush(); } }, [open, loadList, refreshPush]);
  useEffect(() => {
    if (!subscribe) return;
    return subscribe((m) => {
      if (String(m?.type || '').startsWith('notification.')) { loadCount(); if (open) loadList(); }
    });
  }, [subscribe, open, loadCount, loadList]);

  const markRead = async (n) => {
    if (!n.read) {
      try { await api.post(`/notifications/${n.id}/read`); setCount((c) => Math.max(0, c - 1)); setItems((arr) => arr.map((x) => x.id === n.id ? { ...x, read: true } : x)); } catch { /* noop */ }
    }
    if (n.link) window.location.href = n.link;
  };
  const markAll = async () => { try { await api.post('/notifications/read-all'); setCount(0); setItems((arr) => arr.map((x) => ({ ...x, read: true }))); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const remove = async (id) => { try { await api.delete(`/notifications/${id}`); setItems((arr) => arr.filter((x) => x.id !== id)); loadCount(); } catch { /* noop */ } };

  const openCompose = (recipient_id = null, recipient_name = '') => {
    setCompose({ title: '', body: '', recipient_id, recipient_name });
    setComposeOpen(true); setOpen(false);
  };
  const sendMessage = async () => {
    if (!compose.body.trim()) return toast.error('Scrivi un messaggio');
    try {
      await api.post('/notifications/send', {
        title: compose.title.trim() || (isAdmin ? 'Messaggio dall\'amministratore' : 'Messaggio da uno stakeholder'),
        body: compose.body.trim(),
        recipient_id: compose.recipient_id || undefined,
      });
      toast.success('Messaggio inviato');
      setComposeOpen(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="ghost" size="icon" className="relative" data-testid="notifications-bell" aria-label="Notifiche">
            <Bell className="h-5 w-5" />
            {count > 0 && (
              <span data-testid="notifications-badge" className="absolute -right-0.5 -top-0.5 flex h-4.5 min-w-[18px] items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold leading-none text-destructive-foreground">
                {count > 99 ? '99+' : count}
              </span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-[360px] p-0" data-testid="notifications-panel">
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <div className="flex items-center gap-2">
              <p className="font-display text-sm font-bold">Notifiche</p>
              <span className="flex items-center gap-1 rounded-full bg-accent px-2 py-0.5 text-[10px] font-medium text-accent-foreground" title="Contenuti cifrati AES-256">
                <Lock className="h-3 w-3" /> cifrate
              </span>
            </div>
            {count > 0 && (
              <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={markAll} data-testid="notifications-mark-all">
                <CheckCheck className="mr-1 h-3.5 w-3.5" />Segna lette
              </Button>
            )}
          </div>

          {push.supported && (
            <div className="flex items-center justify-between gap-2 border-b border-border bg-muted/30 px-4 py-2.5" data-testid="push-toggle-row">
              <div className="flex items-center gap-2">
                <BellRing className={cn('h-4 w-4', push.subscribed ? 'text-primary' : 'text-muted-foreground')} />
                <div className="leading-tight">
                  <p className="text-xs font-medium">Avvisi push del browser</p>
                  <p className="text-[10px] text-muted-foreground">{push.subscribed ? 'Attivi su questo dispositivo' : 'Ricevili anche ad app chiusa'}</p>
                </div>
              </div>
              <Button
                size="sm"
                variant={push.subscribed ? 'outline' : 'default'}
                className="h-7 shrink-0 text-xs"
                onClick={togglePush}
                disabled={pushBusy}
                data-testid="push-toggle-btn"
              >
                {pushBusy && <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />}
                {push.subscribed ? 'Disattiva' : 'Attiva'}
              </Button>
            </div>
          )}

          <ScrollArea className="max-h-[360px]">
            {items.length === 0 ? (
              <div className="flex flex-col items-center py-12 text-center">
                <div className="mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-muted"><Bell className="h-6 w-6 text-muted-foreground" /></div>
                <p className="text-sm text-muted-foreground">Nessuna notifica</p>
              </div>
            ) : (
              <div className="divide-y divide-border" data-testid="notifications-list">
                {items.map((n) => {
                  const Icon = TYPE_ICON[n.type] || Bell;
                  return (
                    <div key={n.id} className={cn('group flex gap-3 px-4 py-3 transition-colors hover:bg-muted/50', !n.read && 'bg-accent/40')} data-testid="notification-item">
                      <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-accent"><Icon className="h-4 w-4 text-primary" /></div>
                      <button type="button" onClick={() => markRead(n)} className="min-w-0 flex-1 text-left">
                        <p className={cn('truncate text-sm', !n.read ? 'font-semibold' : 'font-medium text-foreground/80')}>{n.title}</p>
                        <p className="line-clamp-2 text-xs text-muted-foreground">{n.body}</p>
                        <p className="mt-0.5 text-[10px] text-muted-foreground">{n.sender_name ? `${n.sender_name} · ` : ''}{timeAgo(n.created_at)}</p>
                      </button>
                      <div className="flex flex-col items-center gap-1">
                        {!n.read && <span className="mt-1 h-2 w-2 rounded-full bg-primary" />}
                        {isAdmin && n.sender_role === 'stakeholder' && n.sender_id && (
                          <Button size="icon" variant="ghost" className="h-6 w-6 opacity-0 group-hover:opacity-100" title="Rispondi" onClick={() => openCompose(n.sender_id, n.sender_name)} data-testid="notification-reply">
                            <MessageSquarePlus className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        <Button size="icon" variant="ghost" className="h-6 w-6 opacity-0 group-hover:opacity-100" title="Elimina" onClick={() => remove(n.id)}>
                          <Trash2 className="h-3.5 w-3.5 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </ScrollArea>

          {!isAdmin && (
            <div className="border-t border-border p-3">
              <Button variant="outline" size="sm" className="w-full" onClick={() => openCompose()} data-testid="notifications-contact-admin">
                <MessageSquarePlus className="mr-2 h-4 w-4" />Scrivi all'amministratore
              </Button>
            </div>
          )}
        </PopoverContent>
      </Popover>

      <Dialog open={composeOpen} onOpenChange={setComposeOpen}>
        <DialogContent data-testid="notifications-compose-dialog">
          <DialogHeader>
            <DialogTitle>{isAdmin ? `Messaggio a ${compose.recipient_name || 'stakeholder'}` : 'Scrivi all\'amministratore'}</DialogTitle>
            <DialogDescription className="flex items-center gap-1"><Lock className="h-3.5 w-3.5" />Il contenuto viene cifrato (AES-256) prima di essere salvato.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2"><Label>Oggetto (opzionale)</Label><Input value={compose.title} onChange={(e) => setCompose({ ...compose, title: e.target.value })} data-testid="compose-title" /></div>
            <div className="space-y-2"><Label>Messaggio</Label><Textarea rows={4} value={compose.body} onChange={(e) => setCompose({ ...compose, body: e.target.value })} data-testid="compose-body" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setComposeOpen(false)}>Annulla</Button>
            <Button onClick={sendMessage} data-testid="compose-send"><Send className="mr-1.5 h-4 w-4" />Invia</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
